import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import type { PostDeploymentInstruction } from "../types/pdm.types";
import config from "../data/config";
import { fetchProviderExplorerDestinationUrl } from "../utils/explorerLinks";

export type PostDeploymentInstructionsViewProps = {
  instructions: PostDeploymentInstruction[];
  context?: {
    network?: string;
    provider?: string;
    deploymentAddress?: string;
    deploymentAddressLinkAddress?: string;
  };
};

function sortInstructions(instructions: PostDeploymentInstruction[]) {
  return instructions
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const byOrder = a.item.order - b.item.order;
      if (byOrder !== 0) return byOrder;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

const DEFAULT_DEPLOYMENT_ADDRESS_FALLBACK = "The deployment has not yet completed";

function interpolateInstructionText(
  raw: string,
  context: PostDeploymentInstructionsViewProps["context"] | undefined,
) {
  const network = context?.network?.trim() ?? "";
  const deploymentAddress =
    context?.deploymentAddress?.trim() || DEFAULT_DEPLOYMENT_ADDRESS_FALLBACK;

  let text = raw;

  if (network) {
    text = text.replaceAll("{network}", network);
  }

  text = text.replaceAll("{deploymentAddress}", deploymentAddress);

  return text;
}

function renderDeploymentAddressLinks(args: {
  raw: string;
  deploymentAddress?: string;
  deploymentAddressUrl?: string | null;
}) {
  const { raw, deploymentAddress, deploymentAddressUrl } = args;

  if (!deploymentAddress) return raw;

  // Support both explicit placeholder and already-interpolated address text.
  const sentinel = raw.includes("{deploymentAddress}")
    ? "{deploymentAddress}"
    : deploymentAddress;
  const parts = raw.split(sentinel);
  if (parts.length === 1) return raw;

  const addressNode = deploymentAddressUrl ? (
    <Link
      href={deploymentAddressUrl}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{ wordBreak: "break-all" }}
    >
      {deploymentAddress}
    </Link>
  ) : (
    deploymentAddress
  );

  return (
    <>
      {parts.map((part, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={idx}>
          {part}
          {idx < parts.length - 1 ? addressNode : null}
        </span>
      ))}
    </>
  );
}

export function PostDeploymentInstructionsView(
  props: PostDeploymentInstructionsViewProps,
) {
  const sorted = sortInstructions(props.instructions);

  const provider = (props.context?.provider ?? props.context?.network)?.trim() ?? "";
  const deploymentAddressLinkAddress =
    props.context?.deploymentAddressLinkAddress?.trim() ?? "";
  const baseUrl = config.PDM_API_BASE_URL.trim();

  const explorerUrlQuery = useQuery({
    queryKey: [
      "abstractionLayerExplorerUrl",
      baseUrl,
      provider,
      deploymentAddressLinkAddress,
    ],
    queryFn: () =>
      fetchProviderExplorerDestinationUrl(
        baseUrl,
        provider,
        deploymentAddressLinkAddress,
      ),
    enabled:
      Boolean(baseUrl) && Boolean(provider) && Boolean(deploymentAddressLinkAddress),
    staleTime: 60 * 60 * 1000,
  });

  return (
    <Box>
      {sorted.length === 0 ? (
        <Typography color="text.secondary">No instructions.</Typography>
      ) : (
        <Stack spacing={2}>
          {sorted.map((instruction) => (
            <Card
              key={`${instruction.order}-${instruction.title}`}
              variant="outlined"
            >
              <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                <Stack spacing={0.75}>
                  <Typography variant="overline" color="text.secondary">
                    Order {instruction.order}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {renderDeploymentAddressLinks({
                      raw: interpolateInstructionText(
                        instruction.title,
                        props.context,
                      ),
                      deploymentAddress: deploymentAddressLinkAddress,
                      deploymentAddressUrl: explorerUrlQuery.data,
                    })}
                  </Typography>
                  {instruction.description && (
                    <Typography variant="body2" color="text.secondary">
                      {renderDeploymentAddressLinks({
                        raw: interpolateInstructionText(
                          instruction.description,
                          props.context,
                        ),
                        deploymentAddress: deploymentAddressLinkAddress,
                        deploymentAddressUrl: explorerUrlQuery.data,
                      })}
                    </Typography>
                  )}
                  {instruction.link && (
                    <Link
                      href={instruction.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ wordBreak: "break-all" }}
                    >
                      {instruction.link}
                    </Link>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default PostDeploymentInstructionsView;
